import { Avatar } from "@/components/Avatar";
import { STATUS_LABEL, STATUS_COLOR, getChannelMeta } from "@/lib/helpers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Fire } from "@phosphor-icons/react";

/**
 * Right-side panel content rendered BELOW the SOFI panel.
 *
 * Layout (top → bottom):
 *   1. Acciones (compacto): Estado + Asignado + Urgencia, todo inline / stack denso.
 *   2. Tags (si existen)
 *   3. Footer fino: cliente y viaje en líneas únicas, texto pequeño gris.
 *
 * SOFI ya no vive aquí — se renderiza arriba del todo desde InboxPage.
 */
export default function ConversationDetails({
    conversation, trip, customer, users, currentUser,
    onChangeStatus, onChangeAssignee, onToggleUrgent,
}) {
    if (!conversation) return null;
    const ch = getChannelMeta(conversation.channel);
    const readOnly = currentUser?.role === "viewer";
    const isUrgent = conversation.urgency === "urgent";
    const statusKey = conversation.status || "open";
    const assignedUser = users.find(x => x.id === conversation.assigned_user_id);

    return (
        <div className="px-4 pt-3 pb-1 space-y-3" data-testid="conversation-details-panel">
            {/* --- ACCIONES (compacto) --- */}
            <section aria-label="Acciones" data-testid="actions-section">
                <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-1.5">
                    Acciones
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                    {/* Estado */}
                    <Select value={statusKey} onValueChange={onChangeStatus} disabled={readOnly}>
                        <SelectTrigger className="w-full h-8 text-xs" data-testid="status-dropdown-trigger">
                            <SelectValue>
                                <span className="inline-flex items-center gap-1.5">
                                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_COLOR[statusKey].dot}`} />
                                    {STATUS_LABEL[statusKey]}
                                </span>
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="open" data-testid="status-option-open">Abierta</SelectItem>
                            <SelectItem value="snoozed" data-testid="status-option-snoozed">Aplazada</SelectItem>
                            <SelectItem value="closed" data-testid="status-option-closed">Cerrada</SelectItem>
                        </SelectContent>
                    </Select>

                    {/* Asignado */}
                    <Select
                        value={conversation.assigned_user_id || "__none__"}
                        onValueChange={(v) => onChangeAssignee(v === "__none__" ? null : v)}
                        disabled={readOnly}
                    >
                        <SelectTrigger className="w-full h-8 text-xs" data-testid="assignee-dropdown-trigger">
                            <SelectValue placeholder="Sin asignar">
                                {!assignedUser
                                    ? <span className="text-slate-500">Sin asignar</span>
                                    : (
                                        <span className="inline-flex items-center gap-1.5 truncate">
                                            <Avatar name={assignedUser.name} color={assignedUser.avatar_color} size={16} />
                                            <span className="truncate">{assignedUser.name}</span>
                                        </span>
                                    )}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="__none__" data-testid="assignee-option-none">Sin asignar</SelectItem>
                            {users.filter(u => u.active && u.role !== "viewer").map(u => (
                                <SelectItem key={u.id} value={u.id} data-testid={`assignee-option-${u.id}`}>
                                    <span className="inline-flex items-center gap-2">
                                        <Avatar name={u.name} color={u.avatar_color} size={18} />
                                        {u.name}
                                    </span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {/* Urgencia: ocupa fila completa */}
                    <Button
                        variant={isUrgent ? "destructive" : "outline"}
                        size="sm"
                        onClick={onToggleUrgent}
                        disabled={readOnly}
                        className={`col-span-2 justify-center h-8 text-xs ${isUrgent ? "vh-pulse" : ""}`}
                        data-testid="toggle-urgent-btn"
                    >
                        <Fire size={13} weight={isUrgent ? "fill" : "regular"} className="mr-1.5" />
                        {isUrgent ? "Marcada urgente" : "Marcar urgente"}
                    </Button>
                </div>
            </section>

            {/* --- TAGS --- */}
            {conversation.tags?.length > 0 && (
                <section data-testid="tags-section">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mb-1.5">
                        Etiquetas
                    </div>
                    <div className="flex flex-wrap gap-1">
                        {conversation.tags.map(t => (
                            <Badge key={t} variant="secondary" className="text-[10px] font-normal py-0" data-testid={`tag-${t}`}>{t}</Badge>
                        ))}
                    </div>
                </section>
            )}

            {/* --- FOOTER FINO: cliente + viaje en líneas únicas --- */}
            <section
                aria-label="Información secundaria"
                className="pt-3 mt-1 border-t border-slate-100 space-y-1 text-[11px] text-slate-500"
                data-testid="customer-trip-footer"
            >
                <div className="flex items-center gap-1.5 truncate" data-testid="footer-customer">
                    <Avatar name={customer?.name} size={14} />
                    <span className="font-medium text-slate-700 truncate" data-testid="details-customer-name">
                        {customer?.name || "Sin cliente"}
                    </span>
                    {customer?.email && (
                        <span className="truncate">· {customer.email}</span>
                    )}
                    {customer?.language && (
                        <span className="uppercase shrink-0">· {customer.language}</span>
                    )}
                </div>
                <div className="flex items-center gap-1.5 truncate" data-testid="footer-trip">
                    <span className="font-semibold uppercase tracking-wider shrink-0" style={{ color: ch.color }}>
                        {ch.label}
                    </span>
                    <span className="truncate text-slate-600" data-testid="details-trip-title">
                        · {trip?.title || "Sin viaje"}
                    </span>
                    {trip?.external_refs?.kimkim_trip_id && (
                        <span className="truncate text-slate-400 shrink-0">· {trip.external_refs.kimkim_trip_id}</span>
                    )}
                </div>
            </section>
        </div>
    );
}
